<template>
  <div class="wrapper" style="min-width: 10em; max-width: 100%;">
    <template v-if="schema.attributes.editing && !disabledForAdminOnly">
      <template v-if="!numberAsSelect">
        <input
          class="form-control"
          :accept="schema.accept"
          :alt="schema.alt"
          :autocomplete="schema.autocomplete"
          :checked="schema.checked"
          :class="schema.fieldClasses"
          :dirname="schema.dirname"
          :files="schema.files"
          :formaction="schema.formaction"
          :formenctype="schema.formenctype"
          :formmethod="schema.formmethod"
          :formnovalidate="schema.formnovalidate"
          :formtarget="schema.formtarget"
          :height="schema.height"
          :id="getFieldID(schema)"
          :inputmode="schema.inputType.toLowerCase()
               === 'number' ? 'numeric':''"
          :list="schema.list"
          :max="schema.max"
          :maxlength="schema.inputType === 'text' ?
               (schema.max ? schema.max : null) : null"
          :min="schema.min"
          :minlength="schema.inputType === 'text' ? schema.min : null "
          :multiple="schema.multiple"
          :nameId="schema.inputName"
          :pattern="schema.pattern"
          :placeholder="schema.placeholder"
          :readonly="schema.readonly"
          :required="schema.required"
          :size="schema.size"
          :src="schema.src"
          :step="schema.step"
          :type="schema.inputType.toLowerCase()"
          :width="schema.width"
          :value="value"
          @input="onInput"
          @blur="onBlur"
          @change="schema.onChange || null"
        />
        <div v-if="schema.inputType === 'text' && schema.max">
          <span
                v-if="schema.max"
                :class="{ 'text-danger': value && value.length > schema.max,
                'text-success': value === null || value.length <= schema.max }">
            Provided <strong>{{ !!value ? value.length : 0 }} out of
              {{ schema.max }}</strong> characters.
          </span>
        </div>
      </template>
      <template v-if="numberAsSelect">
        <select
          class="form-select"
          :class="schema.fieldClasses"
          :height="schema.height"
          :id="getFieldID(schema)"
          :nameId="schema.inputName"
          :readonly="schema.readonly"
          :required="schema.required"
          :size="schema.size"
          :value="value"
          @input="onInput"
          @blur="onBlur"
          @change="schema.onChange || null"
          >
          <option
            v-for="num in numberList"
            :key="num"
            :value="num">
            {{ num }}
          </option>
        </select>
      </template>
    </template>
    <template v-if="!schema.attributes.editing">
      <p
        v-if="!lodash.isNull(value)" class="response"
        v-html="prettyValue"></p>
      <span v-else style="font-style: italic; color: #555">
        Not available
      </span>
    </template>
    <span
      class="helper"
      v-if="schema.inputType.toLowerCase() === 'color'
        || schema.inputType.toLowerCase() === 'range'">
      {{ value }}
    </span>
    <div
      class="form-input-hint text-error"
      v-if="schema.attributes.editing && disabledForAdminOnly">
      Field is restricted to site administrators.
    </div>
  </div>
</template>

<script>
// This is a lightly modified version of vue-form-generator's input field.
import _ from 'lodash';
import moment from 'moment';
import sanitize from 'sanitize-html';

import { MixinDataToReactiveData } from 'assets/js/util';
import { urls2html } from 'assets/js/strings';

import { abstractField } from 'components/Widgets/FormGenerator';
import FieldCommon from './FieldCommon';

const DATETIME_FORMATS = {
  date: 'YYYY-MM-DD',
  datetime: 'YYYY-MM-DD HH:mm:ss',
  'datetime-local': 'YYYY-MM-DDTHH:mm:ss',
};

const NUMBER_SELECT_RANGE_MAX = 25;

export default {
  name: 'field-q-text-input',

  mixins: [abstractField, FieldCommon],

  setup() {
    const afd = MixinDataToReactiveData(abstractField);
    const fcd = MixinDataToReactiveData(FieldCommon);
    return {
      ...afd,
      ...fcd,
    };
  },

  created() {
    if (this.schema.inputType.toLowerCase() === 'file') {
      console.warn('The \'file\' type in input field is deprecated. Use '
          + '\'file\' field instead.');
    }
  },

  computed: {
    numberAsSelect() {
      if (this.schema.inputType.toLowerCase() !== 'number') return false;
      if (!_.isFinite(this.schema.min) || !_.isFinite(this.schema.max)) {
        return false;
      }
      if (this.schema.step === 0) return false; // Infinite steps.
      if (!_.isInteger(this.schema.step)) return false; // Integer steps only.

      return (this.schema.max - this.schema.min) / this.schema.step
        <= NUMBER_SELECT_RANGE_MAX;
    },

    numberList() {
      if (!this.numberAsSelect) return [];
      return _.range(this.schema.min, this.schema.max + 1, this.schema.step);
    },

    // prettyValue replaces value/null with value/(none).
    prettyValue() {
      const opts = {
        allowedTags: [],
        allowedAttributes: [],
        disallowedTagsMode: 'escapeRecursive',
      };
      if (!this.value) return '(none)';
      return urls2html(sanitize(this.value, opts), '_blank');
    },
  },

  data() {
    return {};
  },

  methods: {
    formatDatetimeToModel(newValue, oldValue) {
      let newValueLocal = newValue;
      const defaultFormat = DATETIME_FORMATS[this.schema.inputType
        .toLowerCase()];
      const m = moment(newValueLocal, defaultFormat);
      if (m !== false) {
        if (this.schema.format) {
          newValueLocal = moment(m).format(this.schema.format);
        } else {
          newValueLocal = m.valueOf();
        }
      }
      this.updateModelValue(newValueLocal, oldValue);
    },

    formatNumberToModel(newValue, oldValue) {
      let newValueLocal = newValue;
      if (!_.isNumber(newValueLocal)) {
        newValueLocal = NaN;
      }
      this.updateModelValue(newValueLocal, oldValue);
    },

    formatValueToModel(value) {
      if (value != null) {
        switch (this.schema.inputType.toLowerCase()) {
          case 'date':
          case 'datetime':
          case 'datetime-local':
          case 'range':
            // debounce
            return (newValue, oldValue) => {
              this.debouncedFormatFunc(value, oldValue);
            };
          default:
            break;
        }
      }
      return value;
    },

    onBlur() {
      if (_.isFunction(this.debouncedFormatFunc)) {
        this.debouncedFormatFunc.flush();
      }
    },

    onInput($event) {
      let value = $event.target.value;
      switch (this.schema.inputType.toLowerCase()) {
        case 'range':
          if (_.isNumber(parseFloat($event.target.value))) {
            value = parseFloat($event.target.value);
          }
          break;
        default:
          break;
      }
      this.value = value;
    },
  },

  mounted() {
    switch (this.schema.inputType.toLowerCase()) {
      case 'range':
        this.debouncedFormatFunc = _.debounce(
          (newValue, oldValue) => {
            this.formatNumberToModel(newValue, oldValue);
          },
          parseInt(_.get(this.schema, 'debounceFormatTimeout', 1000), 10),
          {
            trailing: true,
            leading: false,
          },
        );
        break;
      case 'date':
      case 'datetime':
      case 'datetime-local':
        // Wait 1s before calling 'formatDatetimeToModel' to allow user to
        // input data.
        this.debouncedFormatFunc = _.debounce(
          (newValue, oldValue) => {
            this.formatDatetimeToModel(newValue, oldValue);
          },
          parseInt(_.get(this.schema, 'debounceFormatTimeout', 1000), 10),
          {
            trailing: true,
            leading: false,
          },
        );
        break;
      default:
        break;
    }
  },
};
</script>

<style scoped lang="scss">
  @import "~assets/css/colors";

  input[type="text"], input[type="number"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: #fff;
    background-image: none;
    border: 0.1rem solid #c4c9d3;
    border-radius: 0.2rem;
    color: var(--primary-color, $primarycolor);
    display: block;
    font-size: 1.4rem;
    height: 3.2rem;
    line-height: 2rem;
    max-width: 100%;
    outline: 0;
    padding: 0.5rem 0.8rem;
    position: relative;
    transition: all 0.2s ease;
    width: 100%;
  }

  input[type="number"] {
    max-width: 10em;
  }

  input[disabled] {
    background-color: #f0f1f4;
    cursor: not-allowed;
    opacity: .5;
  }

  input[readonly="readonly"] {
    display: none;
  }

  .response {
    &.empty-value {
      font-weight: normal;
      color: $greycolor;
    }
  }

  .text-danger {
    color: $dangercolor;
  }
  .text-success {
    color: $successcolor;
  }

  .dark-mode {
    input[type="text"], input[type="number"] {
      background-color: $dmbglight;
      color: $dmtext;
    }
    input[disabled] {
      background-color: $dmbglight;
    }
  }
</style>
